import { getDateString, getNormalizedDate } from '@/utils/dateUtils';
import { GameMode } from '@/utils/gameState';

export type ModeSelectionSource = 'result_modal' | 'mode_dropdown';

type GuessRejectedReason = 'not_enough_letters' | 'not_in_word_list';
type ShareMethod = 'copy' | 'native';
type ArchiveSelectionType = 'date' | 'random';
type EncorePromptDecision = 'accept' | 'decline';

interface BaseGameEventParams {
  game_mode: GameMode;
}

interface PuzzleScopedEventParams extends BaseGameEventParams {
  level_name: string;
  puzzle_id: string;
  played_date: string;
  puzzle_date?: string;
}

interface AnalyticsEventMap {
  level_start: PuzzleScopedEventParams;
  level_end: PuzzleScopedEventParams & {
    success: boolean;
    attempt_count: number;
  };
  post_score: BaseGameEventParams & {
    score: number;
    attempt_count: number;
    level?: number;
    puzzle_id: string;
    played_date: string;
    puzzle_date?: string;
  };
  guess_submitted: BaseGameEventParams & {
    row_index: number;
  };
  guess_rejected: BaseGameEventParams & {
    reason: GuessRejectedReason;
    row_index: number;
    guess_length: number;
  };
  encore_timeout: BaseGameEventParams & {
    row_index: number;
  };
  share: BaseGameEventParams & {
    method: ShareMethod;
    puzzle_id: string;
    played_date: string;
    puzzle_date?: string;
  };
  mode_selected: {
    from_mode: GameMode;
    to_mode: GameMode;
    source: ModeSelectionSource;
  };
  archive_selected: {
    selection_type: ArchiveSelectionType;
    puzzle_date: string;
  };
  encore_prompt_decision: {
    decision: EncorePromptDecision;
    daily_success: boolean;
    puzzle_id: string;
    played_date: string;
    puzzle_date?: string;
  };
}

type AnalyticsEventName = keyof AnalyticsEventMap;

const isDateBasedMode = (mode: GameMode): boolean => {
  return mode === GameMode.DAILY || mode === GameMode.ARCHIVE;
};

const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

const pruneUndefinedParams = (params: Record<string, unknown>): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined)
  );
};

const resolveDateString = (mode: GameMode, providedDate?: Date | null): string | undefined => {
  if (!isDateBasedMode(mode)) return undefined;
  return getDateString(providedDate ?? getNormalizedDate());
};

export const buildPuzzleId = (mode: GameMode, date?: Date | null): string => {
  if (mode === GameMode.ENCORE) return 'encore';
  if (mode === GameMode.RANDOM) return 'random';

  const dateString = resolveDateString(mode, date);
  if (mode === GameMode.DAILY) return `daily:${dateString}`;
  return `archive:${dateString}`;
};

export const buildPlayedDate = (): string => {
  return getDateString(getNormalizedDate());
};

export const buildPuzzleDate = (mode: GameMode, date?: Date | null): string | undefined => {
  return resolveDateString(mode, date);
};

export const buildLevelName = (mode: GameMode, date?: Date | null): string => {
  return buildPuzzleId(mode, date);
};

export const buildScore = (attemptCount: number, success: boolean): number => {
  if (!success) return 0;
  return 7 - attemptCount;
};

export const buildPostScoreLevel = (
  mode: GameMode,
  targetWordNumber: number | null
): number | undefined => {
  const isDateBasedLevel = mode === GameMode.DAILY || mode === GameMode.ARCHIVE;

  if (!isDateBasedLevel) return undefined;
  if (typeof targetWordNumber !== 'number') return undefined;
  return targetWordNumber;
};

export const trackEvent = <TEventName extends AnalyticsEventName>(
  eventName: TEventName,
  params: AnalyticsEventMap[TEventName]
): void => {
  if (!isGtagAvailable()) return;

  window.gtag?.('event', eventName, pruneUndefinedParams(params as Record<string, unknown>));
};
